/* Treść dokumentu: Regulamin / Polityka / Zwroty (artboard 03a).
 *
 * Rama, zakładki, tytuł i stopka mieszkają w `LegalShell` — dzielonym ze Zgłoszeniem
 * treści, żeby strona zgłoszenia nie mogła odpłynąć w stronę „innej witryny”.
 * Tutaj zostaje spis z podążaniem za przewijaniem i renderer bloków.
 *
 * Numer paragrafu stoi PRZY nagłówku (`§1`), nie w spisie: dwa zapisy tej samej
 * liczby to dwa miejsca, w których może się rozjechać z dokumentem. W arkuszu na 390
 * numer wraca, bo tam nagłówków nie widać (artboard 03b).
 */
import Icon from '../components/Icon'
import { useT } from '../lib/i18n'
import { scrollToSection, useScrollSpy } from '../lib/useScrollSpy'
import LegalShell from '../components/LegalShell'
import DocToc from '../components/public/DocToc'
import type { LegalSection } from './legalData'
import { buildRegulamin, buildPolityka, buildZwroty } from './legalData'

interface Props {
  tab: 'regulamin' | 'polityka' | 'zwroty'
}

// ── Block renderers ────────────────────────────────────────────────────────────

function Block({ b }: { b: LegalSection['blocks'][number] }) {
  if (b.t === 'p') return <p>{b.v}</p>
  if (b.t === 'h3') return <h3>{b.v}</h3>
  if (b.t === 'ul') return <ul>{b.v.map((x, i) => <li key={i}>{x}</li>)}</ul>
  if (b.t === 'note') return (
    <div className="mp-legal-note">
      <Icon name="Info" size={18} />
      <div>{b.v}</div>
    </div>
  )
  if (b.t === 'deftable') return (
    <table className="mp-deftable">
      <tbody>
        {b.rows.map((r, i) => (
          <tr key={i}><td>{r[0]}</td><td>{r[1]}</td></tr>
        ))}
      </tbody>
    </table>
  )
  return null
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function LegalPage({ tab }: Props) {
  const t = useT()

  const build = { regulamin: buildRegulamin, polityka: buildPolityka, zwroty: buildZwroty }[tab]
  const sections = build(t)
  const items = sections.map((s) => ({ id: s.id, num: s.num, label: s.title }))
  const activeId = useScrollSpy(sections.map((s) => s.id))

  return (
    <LegalShell tab={tab} title={t(`legal.${tab}.title`)} version={t(`legal.${tab}.sub`)} date={t(`legal.${tab}.date`)}>
      <DocToc items={items} activeId={activeId} onSelect={scrollToSection}
              meta={t('legal.toc.sections', { n: sections.length })} />

      <article className="mp-legal">
        {sections.map((s) => (
          <section key={s.id} id={s.id}>
            <h2><span className="mp-num" aria-hidden="true">§{s.num}</span>{s.title}</h2>
            {s.blocks.map((b, i) => <Block key={i} b={b} />)}
          </section>
        ))}
      </article>
    </LegalShell>
  )
}
